import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { scrollToSection } from "./Nav";
import { FEATURES } from "../config/features";

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section id="top" ref={ref} className="relative pt-[72px] min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full grid md:grid-cols-12 gap-12 items-center py-24">
        <div className="md:col-span-7 md:order-2">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-base md:text-lg font-bold uppercase tracking-[0.25em] text-moss mb-8 drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)]"
            data-testid="hero-eyebrow"
          >
            A sacred space · Guided by the five elements
          </motion.p>
          <h1 className="font-serif font-light text-[16vw] md:text-[7.5rem] leading-[0.95] tracking-tight" data-testid="hero-heading">
            <MaskedLine delay={0.35}>Rashmi</MaskedLine>
            <MaskedLine delay={0.5}>
              <span className="italic text-terra">Katari</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1 }}
            className="mt-10 max-w-lg text-lg md:text-xl lg:text-2xl leading-relaxed text-ink/95 font-light"
            data-testid="hero-subtitle"
          >
            For healing, grounding, and conscious living — where nature, ritual,
            and inner stillness meet.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/vision"
              data-testid="hero-explore-button"
              className="group bg-ink text-sand px-8 py-4 text-xs uppercase tracking-widest hover:bg-terra transition-colors duration-300 flex items-center gap-3"
            >
              Our Vision
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
            {FEATURES.showJourneyPage ? (
              <Link
                to="/journey"
                data-testid="hero-journey-button"
                className="group px-8 py-4 text-xs uppercase tracking-widest border border-ink/30 hover:border-terra hover:text-terra transition-colors duration-300 flex items-center gap-2"
              >
                The Journey
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            ) : (
              <Link
                to="/contact"
                data-testid="hero-contact-button"
                className="group px-8 py-4 text-xs uppercase tracking-widest border border-ink/30 hover:border-terra hover:text-terra transition-colors duration-300 flex items-center gap-2"
              >
                Begin a Conversation
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            )}
          </motion.div>
        </div>
        <div className="md:col-span-5 relative md:order-1">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden rounded-t-full border border-line max-h-[80vh] md:max-h-none"
          >
            <motion.div style={{ y: imgY }} className="relative w-full h-[60vh] md:h-[75vh] scale-110">
              <img
                src={`${process.env.PUBLIC_URL}/art/hero-portrait.png?v=3`}
                alt="Portrait of Rashmi Katari"
                className="w-full h-full object-cover"
                style={{ objectPosition: "center center" }}
                data-testid="hero-image"
              />
            </motion.div>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 1 }}
            className="absolute -bottom-6 -right-6 md:-right-12 font-serif italic text-xl text-moss font-medium bg-sand px-5 py-2.5 border border-line drop-shadow-[0_1px_3px_rgba(0,0,0,0.85)]"
          >
            est. in devotion
          </motion.p>
        </div>
      </div>
    </section>
  );
}
